import type { IconProps } from "@tamagui/helpers-icon";
import { ChevronDown, ChevronUp } from "@tamagui/lucide-icons";
import type { ComponentType } from "react";

import { Button, Popover, Text, XStack, YStack } from "@/components";
import { getShadow } from "@/theme/helpers";
import { isMobileWeb } from "@/utils/web";

interface DeviceSelectorProps {
  devices: MediaDeviceInfo[];
  selectedDevice: string | undefined;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onDeviceChange: (deviceId: string) => void;
  icon: ComponentType<IconProps>;
  iconOff: ComponentType<IconProps>;
  isDisabled: boolean;
  onToggle: () => void;
  ariaLabel?: string;
  toggleAriaLabel?: string;
}

const DeviceSelector = ({
  devices,
  selectedDevice,
  isOpen,
  onOpenChange,
  onDeviceChange,
  icon: Icon,
  iconOff: IconOff,
  isDisabled,
  onToggle,
  ariaLabel,
  toggleAriaLabel,
}: DeviceSelectorProps) => {
  const ActiveIcon = isDisabled ? IconOff : Icon;
  const iconColor = isDisabled ? "$red" : "$fillPrimary";
  const borderColor = isDisabled ? "$red" : "$fillPrimary";
  const isMobile = isMobileWeb();

  return (
    <XStack
      flex={1}
      borderWidth="$strokeWidth"
      borderColor={borderColor}
      borderRadius="$150"
      backgroundColor="$fillInput"
      overflow="hidden"
    >
      <Button.Wrapper
        borderWidth={0}
        paddingHorizontal="$150"
        paddingVertical="$100"
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        backgroundColor="$transparent"
        hoverStyle={{
          backgroundColor: "$navHover",
        }}
        onPress={onToggle}
        aria-label={toggleAriaLabel}
      >
        <ActiveIcon size={20} color={iconColor} />
      </Button.Wrapper>

      {!isMobile && (
        <Popover open={isOpen} onOpenChange={onOpenChange}>
          <Popover.Trigger asChild>
            <Button.Wrapper
              borderWidth={0}
              paddingHorizontal="$150"
              paddingVertical="$100"
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              backgroundColor="$transparent"
              hoverStyle={{
                backgroundColor: "$navHover",
              }}
              aria-label={ariaLabel}
            >
              {isOpen ? (
                <ChevronUp size="$200" color="$fillPrimary" />
              ) : (
                <ChevronDown size="$200" color="$fillPrimary" />
              )}
            </Button.Wrapper>
          </Popover.Trigger>

          <Popover.Content
            borderRadius="$150"
            backgroundColor="$fillCallout"
            {...getShadow()}
            zIndex={999999}
            padding="$100"
          >
            <YStack gap="$50" minWidth={200} maxHeight={300}>
              {devices.map((device) => (
                <Popover.Close asChild key={device.deviceId}>
                  <Button
                    dataTrackingId="video-device-option"
                    onPress={() => onDeviceChange(device.deviceId)}
                    mode="subMenu"
                    role="menuitem"
                    backgroundColor={
                      selectedDevice === device.deviceId
                        ? "$navSelected"
                        : "$transparent"
                    }
                  >
                    <Text size="$sm" color="$textPrimary">
                      {device.label}
                    </Text>
                  </Button>
                </Popover.Close>
              ))}
            </YStack>
          </Popover.Content>
        </Popover>
      )}
    </XStack>
  );
};

export default DeviceSelector;
